import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../utils/dateHelpers';
import pb from '../lib/pocketbaseClient';

export default function Investments() {
  const [transactions, setTransactions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPartner, setFilterPartner] = useState('All');
  const [filterProject, setFilterProject] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [formData, setFormData] = useState({
    partner: '',
    project: '',
    amount: '',
    type: 'deposit',
    date: '',
    note: '',
    reference: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      const [transRes, partnersRes, projectsRes] = await Promise.all([
        pb.collection('investments').getFullList({ sort: '-date', expand: 'partner,project' }),
        pb.collection('partners').getFullList({ sort: 'name' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
      ]);
      if (isMounted.current) {
        setTransactions(transRes);
        setPartners(partnersRes);
        setProjects(projectsRes);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const data = {
        partner: formData.partner,
        project: formData.project || null,
        amount: parseFloat(formData.amount) || 0,
        type: formData.type,
        date: formData.date,
        note: formData.note || null,
        reference: formData.reference || null,
      };

      if (editingTransaction) {
        await pb.collection('investments').update(editingTransaction.id, data);
      } else {
        await pb.collection('investments').create(data);
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      let msg = err.message || 'Failed to save transaction';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join('; ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  const handleEdit = (trans) => {
    setEditingTransaction(trans);
    setFormData({
      partner: trans.partner || '',
      project: trans.project || '',
      amount: trans.amount || '',
      type: trans.type || 'deposit',
      date: trans.date || '',
      note: trans.note || '',
      reference: trans.reference || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await pb.collection('investments').delete(id);
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTransaction(null);
    setFormData({ partner: '', project: '', amount: '', type: 'deposit', date: '', note: '', reference: '' });
    setFormError('');
    setFormLoading(false);
  };

  const getTypeBadge = (type) => {
    if (type === 'deposit') return <Badge className="bg-green-100 text-green-700">⬆ Deposit</Badge>;
    return <Badge className="bg-red-100 text-red-700">⬇ Withdrawal</Badge>;
  };

  const filteredTransactions = transactions.filter(t => {
    const matchPartner = filterPartner === 'All' || t.partner === filterPartner;
    const matchProject = filterProject === 'All' || t.project === filterProject;
    const matchType = filterType === 'All' || t.type === filterType;
    const matchSearch = t.note?.toLowerCase().includes(search.toLowerCase()) ||
                        t.reference?.toLowerCase().includes(search.toLowerCase()) ||
                        t.expand?.partner?.name?.toLowerCase().includes(search.toLowerCase()) ||
                        t.expand?.project?.name?.toLowerCase().includes(search.toLowerCase());
    const matchDate = (!dateFrom || t.date >= dateFrom) && (!dateTo || t.date <= dateTo);
    return matchPartner && matchProject && matchType && matchSearch && matchDate;
  });

  const totalDeposits = filteredTransactions.filter(t => t.type === 'deposit').reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalWithdrawals = filteredTransactions.filter(t => t.type === 'withdrawal').reduce((sum, t) => sum + (t.amount || 0), 0);
  const netBalance = totalDeposits - totalWithdrawals;

  const partnerSummary = partners.map(p => {
    const deposits = transactions.filter(t => t.partner === p.id && t.type === 'deposit').reduce((s, t) => s + (t.amount || 0), 0);
    const withdrawals = transactions.filter(t => t.partner === p.id && t.type === 'withdrawal').reduce((s, t) => s + (t.amount || 0), 0);
    return { ...p, deposits, withdrawals, net: deposits - withdrawals };
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div><p className="ml-3 text-slate-500">Loading...</p></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">🏦 Fund / Investments</h1>
          <p className="text-sm text-slate-500">Track partner investments and withdrawals by project</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => { setEditingTransaction(null); setShowModal(true); }}>+ Add Transaction</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Total Deposits</p><p className="text-2xl font-bold text-green-600">₹{totalDeposits.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Total Withdrawals</p><p className="text-2xl font-bold text-red-600">₹{totalWithdrawals.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Net Balance</p><p className={`text-2xl font-bold ${netBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>₹{netBalance.toLocaleString()}</p></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold text-slate-800 mb-3">🤝 Partner-wise Investment Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {partnerSummary.length === 0 ? <p className="text-slate-500 text-sm col-span-full">No partners found.</p> :
              partnerSummary.map(p => (
                <div key={p.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <p className="font-medium text-slate-800">{p.name}</p>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                    <div><span className="text-slate-500">Deposits</span><br/><span className="font-bold text-green-600">₹{p.deposits.toLocaleString()}</span></div>
                    <div><span className="text-slate-500">Withdrawals</span><br/><span className="font-bold text-red-600">₹{p.withdrawals.toLocaleString()}</span></div>
                    <div><span className="text-slate-500">Net</span><br/><span className={`font-bold ${p.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>₹{p.net.toLocaleString()}</span></div>
                  </div>
                </div>
              ))
            }
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border">
        <div className="flex-1 min-w-[200px]"><Input placeholder="🔍 Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full" /></div>
        <select value={filterPartner} onChange={(e) => setFilterPartner(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400">
          <option value="All">All Partners</option>
          {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400">
          <option value="All">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400">
          <option value="All">All Types</option>
          <option value="deposit">Deposit</option>
          <option value="withdrawal">Withdrawal</option>
        </select>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
          <span className="text-slate-400">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
        </div>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterPartner('All'); setFilterProject('All'); setFilterType('All'); setDateFrom(''); setDateTo(''); }}>Clear</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Partner</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Project</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Type</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Amount</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Note</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Ref</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredTransactions.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-8 text-center text-slate-500">No transactions found.</td></tr>
              ) : (
                filteredTransactions.map(t => (
                  <tr key={t.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-6 py-3">{formatDate(t.date)}</td>
                    <td className="px-6 py-3 font-medium">{t.expand?.partner?.name || 'N/A'}</td>
                    <td className="px-6 py-3">{t.expand?.project?.name || 'N/A'}</td>
                    <td className="px-6 py-3">{getTypeBadge(t.type)}</td>
                    <td className={`px-6 py-3 text-right font-mono font-bold ${t.type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                      {t.type === 'deposit' ? '+' : '-'}₹{t.amount?.toLocaleString()}
                    </td>
                    <td className="px-6 py-3">{t.note || '-'}</td>
                    <td className="px-6 py-3 text-xs">{t.reference || '-'}</td>
                    <td className="px-6 py-3 text-center space-x-2">
                      <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => handleEdit(t)}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(t.id)}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">{editingTransaction ? '✏️ Edit Transaction' : '➕ Add Transaction'}</h2>
              {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">{formError}</div>}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Partner *</label>
                    <select name="partner" value={formData.partner} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                      <option value="">Select</option>
                      {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                    <select name="type" value={formData.type} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                      <option value="deposit">⬆ Deposit</option>
                      <option value="withdrawal">⬇ Withdrawal</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
                  <select name="project" value={formData.project} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label><Input name="amount" type="number" value={formData.amount} onChange={handleChange} required step="0.01" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Date *</label><Input name="date" type="date" value={formData.date} onChange={handleChange} required /></div>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Note</label><Input name="note" value={formData.note} onChange={handleChange} /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Reference</label><Input name="reference" value={formData.reference} onChange={handleChange} /></div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>{formLoading ? 'Saving...' : (editingTransaction ? 'Update' : 'Create')}</Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

